import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { CellContext, ColumnDef, createColumnHelper, RowData } from '@tanstack/react-table'

import { IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch } from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SettingsIcon from '@mui/icons-material/Settings'
import PersonRemoveAlt1RoundedIcon from '@mui/icons-material/PersonRemoveAlt1Rounded'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'

import ConfirmationDialog from '@/components/ui/dialogs/ConfirmationDialog'
import Table, { TableProps } from '@/components/ui/tables/Table'

import { useRemoveNetworkMembersMutation, useUpdateMemberAccessLevelMutation } from '@/core/services/network'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from '@/utils/errors'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    removeMember?: (memberId: string) => void
    updateMemberAccessLevel?: (memberId: string, isAdmin: boolean) => void
  }
}

export type MemberData = {
  id: string
  canLeave: boolean
  canRemove: boolean
  canPromote: boolean
  email: string
  establishment?: string
  invitedBy?: string
  name: string
  role: string
  isAdmin: boolean
}

export type MembersTableProps = Pick<TableProps<MemberData>, 'data' | 'loading' | 'onDeleteMany' | 'selectable'> & {
  extraColumns?: ColumnDef<MemberData, any>[]
  onDeleteClick?: (id: string) => void
  onOpenClick?: (licenseId: string) => void
  onRemoveMember?: (memberId: string) => void
  onUpdateMemberAccessLevel?: (memberId: string, isAdmin: boolean) => void
}

export const columnHelper = createColumnHelper<MemberData>()

export function MembersTable(props: MembersTableProps) {
  const { data, extraColumns = [], loading, onDeleteClick, onDeleteMany, onOpenClick, onRemoveMember, selectable } = props

  const { t } = useTranslation()

  const [openDialog, setOpenDialog] = useState(false)
  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('table.networkMembers.header.name').toString(),
      }),
      columnHelper.accessor('email', {
        header: t('table.networkMembers.header.email').toString(),
      }),
      columnHelper.accessor('establishment', {
        header: t('table.networkMembers.header.establishment').toString(),
      }),
      columnHelper.accessor('role', {
        header: t('table.networkMembers.header.accessLevel').toString(),
      }),
      columnHelper.accessor('invitedBy', {
        header: t('table.networkMembers.header.invitedBy').toString(),
      }),
      ...extraColumns,
      columnHelper.display({
        id: 'actions',
        header: ({ table }) => (
          <Stack alignItems="center" justifyContent="center">
            <PopupState variant="popover" popupId="groups-table-configuration">
              {(popupState) => (
                <>
                  <IconButton {...bindTrigger(popupState)}>
                    <SettingsIcon />
                  </IconButton>
                  <Menu
                    {...bindMenu(popupState)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <List>
                      {table
                        .getAllLeafColumns()
                        .filter((column) => column.id !== 'actions')
                        .map((column) => (
                          <ListItem key={column.id}>
                            <ListItemText id={column.id} primary={column.columnDef.header} />
                            <Switch edge="end" onChange={column.getToggleVisibilityHandler()} checked={column.getIsVisible()} />
                          </ListItem>
                        ))}
                    </List>
                  </Menu>
                </>
              )}
            </PopupState>
          </Stack>
        ),
        cell: ActionsCell,
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ]
  }, [extraColumns, t])

  return (
    <>
      <Table
        key="network-members"
        columns={columns}
        data={data}
        loading={loading}
        meta={{
          open: onOpenClick,
          delete: onDeleteClick,
          removeMember: onRemoveMember,
        }}
        onDeleteMany={onDeleteMany}
        selectable={selectable}
      />
      <ConfirmationDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false)
        }}
        onSubmit={() => { }}
        title={t('dialog.confirmation.removeNetworkMembers.title')}
        message={t('dialog.confirmation.removeNetworkMembers.message')}
      />
    </>
  )
}

function ActionsCell({ row, table }: CellContext<MemberData, unknown>) {
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [removeMembers, { isLoading: isRemovingMembers }] = useRemoveNetworkMembersMutation()
  const [updateMemberAccessLevel, { isLoading: isUpdatingAccessLevel }] = useUpdateMemberAccessLevelMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [openPromoteDialog, setOpenPromoteDialog] = useState(false)

  const isAdmin = row.original.isAdmin
  const canPromote = row.original.canPromote

  return (
    <Stack justifyContent="flex-end" direction="row" spacing={1}>
      <PopupState variant="popover" popupId={`group-${row.id}-actions`}>
        {(popupState) => (
          <>
            <IconButton {...bindTrigger(popupState)}>
              <MoreHorizIcon />
            </IconButton>
            <Menu {...bindMenu(popupState)} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem
                onClick={() => {
                  table.options.meta?.open?.(row.original.id)
                }}
              >
                <ListItemIcon>
                  <LaunchIcon />
                </ListItemIcon>
                <ListItemText>{t('common:button.details.label')}</ListItemText>
              </MenuItem>
              {row.original.canRemove && (
                <MenuItem
                  onClick={() => {
                    setOpenDialog(true)
                    popupState.close()
                  }}
                >
                  <ListItemIcon>
                    <PersonRemoveAlt1RoundedIcon />
                  </ListItemIcon>
                  <ListItemText>{t('common:button.remove.label')}</ListItemText>
                </MenuItem>
              )}
              {canPromote && (
                <MenuItem
                  onClick={() => {
                    setOpenPromoteDialog(true)
                    popupState.close()
                  }}
                >
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText>
                    {isAdmin
                      ? t('common:button.demote.label', 'Demote from Admin')
                      : t('common:button.promote.label', 'Promote to Admin')}
                  </ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </PopupState>
      <ConfirmationDialog
        isSubmitting={isRemovingMembers}
        open={openDialog}
        onClose={() => {
          setOpenDialog(false)
        }}
        onSubmit={async () => {
          if (!networkId) return
          try {
            await removeMembers({ networkId, members: [row.original.id] }).unwrap()
            setOpenDialog(false)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.removeMember.error')), { variant: 'error' })
          }
        }}
        title={t('dialog.confirmation.removeNetworkMembers.title')}
        message={t('dialog.confirmation.removeNetworkMembers.message')}
      />
      <ConfirmationDialog
        isSubmitting={isUpdatingAccessLevel}
        open={openPromoteDialog}
        onClose={() => {
          setOpenPromoteDialog(false)
        }}
        onSubmit={async () => {
          if (!networkId) return
          try {
            const newAccessLevel = isAdmin ? 'MEMBER' : 'ADMIN'
            await updateMemberAccessLevel({
              networkId,
              memberId: row.original.id,
              accessLevel: newAccessLevel
            }).unwrap()
            setOpenPromoteDialog(false)
            enqueueSnackbar(
              isAdmin
                ? t('notification.network.demoteMember.success', 'Member demoted successfully')
                : t('notification.network.promoteMember.success', 'Member promoted to admin successfully'),
              { variant: 'success' }
            )
          } catch (error) {
            enqueueSnackbar(
              getApiErrorMessage(
                error,
                isAdmin
                  ? t('notification.network.demoteMember.error', 'Failed to demote member')
                  : t('notification.network.promoteMember.error', 'Failed to promote member')
              ),
              { variant: 'error' }
            )
          }
        }}
        title={
          isAdmin
            ? t('dialog.confirmation.demoteNetworkMember.title', 'Demote Network Member')
            : t('dialog.confirmation.promoteNetworkMember.title', 'Promote Network Member')
        }
        message={
          isAdmin
            ? t('dialog.confirmation.demoteNetworkMember.message', 'Are you sure you want to demote this member from admin? They will no longer have administrative privileges.')
            : t('dialog.confirmation.promoteNetworkMember.message', 'Are you sure you want to promote this member to admin? They will have full administrative privileges for the network.')
        }
      />
    </Stack>
  )
}

export default MembersTable
